import { alpha } from '@mui/material/styles';
import { purple, yellow, blue, cyan, pink } from '@mui/material/colors';
// The portal's own accent, the one the rail, the table heads and the team
// buttons already wear. `portals.js` imports nothing, so there is no cycle.
import { INDIGO_BRIGHT, INDIGO_INK } from 'src/layouts/auth/portals';

// ----------------------------------------------------------------------

function createGradient(color1, color2) {
  return `linear-gradient(to bottom, ${color1}, ${color2})`;
}

// SETUP COLORS
const PRIMARY = {
  lighter: '#C8FACD',
  light: '#5BE584',
  main: '#00AB55',
  dark: '#007B55',
  darker: '#005249'
};
const SECONDARY = {
  lighter: '#D6E4FF',
  light: '#84A9FF',
  main: '#3366FF',
  dark: '#1939B7',
  darker: '#091A7A'
};
const INFO = {
  lighter: '#D0F2FF',
  light: '#74CAFF',
  main: '#1890FF',
  dark: '#0C53B7',
  darker: '#04297A'
};
const SUCCESS = {
  lighter: '#E9FCD4',
  light: '#AAF27F',
  main: '#54D62C',
  dark: '#229A16',
  darker: '#08660D'
};
const WARNING = {
  lighter: '#FFF7CD',
  light: '#FFE16A',
  main: '#FFC107',
  dark: '#B78103',
  darker: '#7A4F01'
};
const ERROR = {
  lighter: '#FFE7D9',
  light: '#FFA48D',
  main: '#FF4842',
  dark: '#B72136',
  darker: '#7A0C2E'
};
// A proper ramp. It used to read lighter/light = #8e24aa (dark) and
// dark/darker = #ba68c8 (light), inverted, which made a ghost Label unreadable
// in light mode — the variant paints the DARK step as its ink.
const PURPLE = {
  lighter: '#F3E5F5',
  light: '#CE93D8',
  main: purple[500],
  dark: '#7B1FA2',
  darker: '#4A148C'
};

const PINK = {
  lighter: pink[500],
  light: pink[500],
  main: pink[500],
  dark: pink[500],
  darker: pink[500]
};

const ATLAS = {
  lighter: '#a56bd8',
  light: '#7f3fbf',
  main: '#6400a9',
  dark: '#4f0085',
  darker: '#35005a'
};

const FUSION = {
  lighter: '#a56bd8',
  light: '#7f3fbf',
  main: '#6400a9',
  dark: '#4f0085',
  darker: '#35005a'
};
const ACADEMY = {
  lighter: '#4caf50',
  light: '#4caf50',
  dark: '#4caf50',
  main: '#2E7D32'
};
const CONSEGNA = {
  lighter: '#009688',
  light: '#009688',
  dark: '#009688',
  main: '#009688'
};
const STUDENTS = {
  lighter: '#43A047',
  light: '#43A047',
  dark: '#43A047',
  main: '#43A047'
};
const TEENAGERS = {
  lighter: '#66BB6A',
  light: '#66BB6A',
  dark: '#66BB6A',
  main: '#66BB6A'
};
const KIDS = {
  lighter: '#8BC34A',
  light: '#8BC34A',
  dark: '#8BC34A',
  main: '#8BC34A'
};
const LINGUIST_CERTIFICATIONS = {
  lighter: '#1B5E20',
  light: '#1B5E20',
  dark: '#1B5E20',
  main: '#1B5E20'
};
const BUSINESS_BEYOND = {
  lighter: '#145A32',
  light: '#145A32',
  dark: '#145A32',
  main: '#145A32'
};

const UNIVERSE = {
  lighter: '#4d5da8',
  light: '#273f8f',
  main: '#001266',
  dark: '#000d4a',
  darker: '#00072f'
};
const TECHNOLOGIES = {
  lighter: '#426bc3',
  light: '#426bc3',
  main: '#263c8f',
  dark: '#263c8f',
  darker: '#263c8f'
};
const CONGRESSI = {
  lighter: '#516f9d',
  light: '#516f9d',
  main: '#2c4a7a',
  dark: '#2c4a7a',
  darker: '#2c4a7a'
};
const CRONOSTORIA = {
  lighter: '#414042',
  light: '#414042',
  main: '#1c1b1c',
  dark: '#1c1b1c',
  darker: '#1c1b1c'
};
const LAYOUT = {
  lighter: '#4f7fb8',
  light: '#1d5f9c',
  main: '#00347a',
  dark: '#00295f',
  darker: '#001c42'
};

const BLUE = {
  lighter: blue[600],
  light: blue[500],
  main: blue[500],
  dark: blue[400],
  darker: blue[400]
};

const YELLOW = {
  lighter: yellow[500],
  light: yellow[500],
  main: yellow[500],
  dark: yellow[500],
  darker: yellow[500]
};

const CYAN = {
  lighter: cyan[500],
  light: cyan[500],
  main: cyan[500],
  dark: cyan[500],
  darker: cyan[500]
};

const GREY = {
  0: '#FFFFFF',
  100: '#F9FAFB',
  200: '#F4F6F8',
  300: '#DFE3E8',
  400: '#C4CDD5',
  500: '#919EAB',
  600: '#637381',
  700: '#454F5B',
  800: '#212B36',
  900: '#161C24',
  500_8: alpha('#919EAB', 0.08),
  500_12: alpha('#919EAB', 0.12),
  500_16: alpha('#919EAB', 0.16),
  500_24: alpha('#919EAB', 0.24),
  500_32: alpha('#919EAB', 0.32),
  500_48: alpha('#919EAB', 0.48),
  500_56: alpha('#919EAB', 0.56),
  500_80: alpha('#919EAB', 0.8)
};

const GRADIENTS = {
  primary: createGradient(PRIMARY.light, PRIMARY.main),
  info: createGradient(INFO.light, INFO.main),
  success: createGradient(SUCCESS.light, SUCCESS.main),
  warning: createGradient(WARNING.light, WARNING.main),
  error: createGradient(ERROR.light, ERROR.main)
};

const CHART_COLORS = {
  violet: ['#826AF9', '#9E86FF', '#D0AEFF', '#F7D2FF'],
  blue: ['#2D99FF', '#83CFFF', '#A5F3FF', '#CCFAFF'],
  green: ['#2CD9C5', '#60F1C8', '#A4F7CC', '#C0F2DC'],
  yellow: ['#FFE700', '#FFEF5A', '#FFF7AE', '#FFF3D6'],
  red: ['#FF6C40', '#FF8F6D', '#FFBD98', '#FFF2D4']
};

const COMMON = {
  common: { black: '#000', white: '#fff' },
  primary: { ...PRIMARY, contrastText: '#fff' },
  secondary: { ...SECONDARY, contrastText: '#fff' },
  info: { ...INFO, contrastText: '#fff' },
  success: { ...SUCCESS, contrastText: GREY[800] },
  warning: { ...WARNING, contrastText: GREY[800] },
  error: { ...ERROR, contrastText: '#fff' },
  purple: { ...PURPLE, contrastText: '#fff' },
  pink: { ...PINK, contrastText: '#fff' },
  atlas: { ...ATLAS, contrastText: '#fff' },
  fusion: { ...FUSION, contrastText: '#fff' },
  universe: { ...UNIVERSE, contrastText: '#fff' },
  academy: { ...ACADEMY, contrastText: '#fff' },
  consegna: { ...CONSEGNA, contrastText: '#fff' },
  students: { ...STUDENTS, contrastText: '#fff' },
  teenagers: { ...TEENAGERS, contrastText: '#fff' },
  kids: { ...KIDS, contrastText: '#fff' },
  'linguist-certifications': {
    ...LINGUIST_CERTIFICATIONS,
    contrastText: '#fff'
  },
  'business-beyond': { ...BUSINESS_BEYOND, contrastText: '#fff' },
  layout: { ...LAYOUT, contrastText: '#fff' },
  technologies: { ...TECHNOLOGIES, contrastText: '#fff' },
  cronostoria: { ...CRONOSTORIA, contrastText: '#fff' },
  congressi: { ...CONGRESSI, contrastText: '#fff' },
  blue: { ...BLUE, contrastText: '#fff' },
  yellow: { ...YELLOW, contrastText: '#fff' },
  cyan: { ...CYAN, contrastText: cyan[50] },
  grey: GREY,
  gradients: GRADIENTS,
  chart: CHART_COLORS,
  divider: GREY[500_24],
  action: {
    hover: GREY[500_8],
    selected: GREY[500_16],
    disabled: GREY[500_80],
    disabledBackground: GREY[500_24],
    focus: GREY[500_24],
    hoverOpacity: 0.08,
    disabledOpacity: 0.48
  }
};

const palette = {
  light: {
    ...COMMON,
    mode: 'light',
    text: {
      primary: GREY[800],
      secondary: GREY[600],
      disabled: GREY[500],
      // Black Olive, the corporate ink — the Typography override paints it on
      // every Typography, so pure black would read heavier than the design.
      default: '#3C3C3B'
    },
    // A whisper of lavender under the white cards, so they float on a
    // hairline instead of a heavy shadow — the light side of the cosmic rail.
    background: { paper: '#fff', default: '#F6F5FA', neutral: GREY[200] },
    chips: { default: '#000' },
    logs: { default: '#ffff' },
    labels: { default: GREY[500] },
    action: { active: GREY[600], ...COMMON.action },
    // A LINK IS INDIGO, NOT MINIMAL GREEN (05/10/2026). Two faults made every
    // link on every record page render in `primary.main` — the Minimal theme's
    // #00AB55, the one hue the brand sheet says must never be an accent, and
    // the same green the portal uses to mean "success": a confirmed
    // allocation, a healthy margin, a delivered project. On a project page the
    // client's name, the quote number and "Open the client link" read as three
    // more green status marks.
    //
    // Fault one is here: `BLUE` has no `700` key (it is lighter/light/main/
    // dark/darker), so this token was `undefined`. Fault two is in
    // overrides/Link.js, where the colour was set on the `root` class that
    // MUI's own `color="primary"` default overrides anyway.
    link: { default: INDIGO_INK }
  },
  dark: {
    ...COMMON,
    mode: 'dark',
    text: {
      primary: '#fff',
      secondary: GREY[500],
      disabled: GREY[600],
      default: 'white'
    },
    // `default` is the page ground behind every card in dark mode. GREY[900]
    // (#161C24) read as a flat blue-grey that clashed with the always-dark
    // cosmic sidebar (#070612); this deep indigo-black sits in the same family
    // as the rail, so the workspace and the sidebar read as one night sky, and
    // the GREY[800] cards still lift clearly off it.
    background: { paper: GREY[800], default: '#0B0A16', neutral: GREY[500_16] },
    chips: { default: '#fff' },
    logs: { default: '#0A2230' },
    labels: { default: GREY[500] },
    action: { active: GREY[500], ...COMMON.action },
    link: { default: INDIGO_BRIGHT }
  }
};

export default palette;
